import { useEffect, useRef, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { CostEstimationResponse, CostFormInput } from './types';

/**
 * Keeps a cost estimate in step with the form, without ever getting in its way.
 *
 * Three rules shape this hook. It runs by itself — there is no Retry button,
 * because a user who cannot price their app can still submit it. It never lets
 * an older answer overwrite a newer one, which is what makes a fast typist see
 * their own numbers instead of somebody's stale ones. And a failure leaves the
 * last good estimate on screen rather than blanking it: a stale price is more
 * use than no price, as long as the UI says which it is.
 */

const DEBOUNCE_MS = 350;

/**
 * Only the nine fields that change an Azure bill. Reading the whole form here
 * would re-price on every keystroke in the app-name box.
 */
export function toCostFormInput(
  formData: Record<string, unknown> | undefined,
): CostFormInput {
  const data = formData ?? {};
  const environments = Array.isArray(data.environments)
    ? (data.environments as string[])
    : [];

  return {
    environments: environments as CostFormInput['environments'],
    enable_static_web: data.enable_static_web === true,
    ...(typeof data.frontend === 'string' && data.frontend
      ? { frontend: data.frontend }
      : {}),
    enable_app_service: data.enable_app_service === true,
    enable_container_app_jobs: data.enable_container_app_jobs === true,
    enable_job_logs: data.enable_job_logs === true,
    enable_postgres: data.enable_postgres === true,
    enable_object_storage: data.enable_object_storage === true,
    enable_key_vault: data.enable_key_vault === true,
    // The App Configuration tier. Day-1 answers "Do you need Parameter
    // Storage?"; Day-2 carries the policy the app is on. Left out when the form
    // says neither, so an app from before the choice existed is priced as what
    // it runs — Standard — rather than as the Day-1 default.
    ...(typeof data.enable_parameter_storage === 'boolean'
      ? { enable_parameter_storage: data.enable_parameter_storage }
      : {}),
    ...(typeof data.app_config_tier_policy === 'string' &&
    data.app_config_tier_policy
      ? { app_config_tier_policy: data.app_config_tier_policy }
      : {}),
  };
}

/** A Day-2 form is the one carrying an untouched snapshot of the app as it is. */
export function readOriginal(
  formData: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const original = formData?._original;
  return typeof original === 'object' &&
    original !== null &&
    !Array.isArray(original)
    ? (original as Record<string, unknown>)
    : undefined;
}

export interface CostEstimationState {
  data?: CostEstimationResponse;
  /** True only while the first estimate is being fetched. */
  loading: boolean;
  /** True while a newer estimate replaces one already on screen. */
  updating: boolean;
  error?: string;
}

export function useCostEstimation(
  formData: Record<string, unknown> | undefined,
): CostEstimationState {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [state, setState] = useState<CostEstimationState>({
    loading: true,
    updating: false,
  });

  const original = readOriginal(formData);
  const request = {
    after: toCostFormInput(formData),
    ...(original ? { before: toCostFormInput(original) } : {}),
  };
  // The request itself is the dependency: identical inputs must not refetch,
  // however many times React re-renders around them.
  const requestKey = JSON.stringify(request);

  const abortRef = useRef<AbortController | undefined>(undefined);
  const latestRef = useRef(0);

  useEffect(() => {
    const sequence = ++latestRef.current;
    setState(previous => ({
      ...previous,
      loading: previous.data === undefined,
      updating: previous.data !== undefined,
    }));

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const baseUrl = await discoveryApi.getBaseUrl('auth0-onboarding');
        const response = await fetchApi.fetch(`${baseUrl}/cost-estimation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: requestKey,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Estimate failed (HTTP ${response.status})`);
        }
        const data = (await response.json()) as CostEstimationResponse;

        // A response that arrived after a newer request went out is history.
        if (sequence !== latestRef.current) return;
        setState({ data, loading: false, updating: false });
      } catch (error) {
        if (controller.signal.aborted || sequence !== latestRef.current) return;
        // Keep whatever was already on screen; only say that it is now stale.
        setState(previous => ({
          data: previous.data,
          loading: false,
          updating: false,
          error: (error as Error).message,
        }));
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [requestKey, discoveryApi, fetchApi]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return state;
}
